import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Stack,
	Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { IInstallmentProposal } from "../../../../../shared/services/api/proposal/ProposalService";
import { globalFormatCurrencyBRL } from "../../../../../shared/utils";
import { globalTranslateStatus } from "../../../../../shared/utils/global-translate";
import { StatusInstallmentChip } from "../../../../../shared/components/StatusInstallmentChip";

interface ModalSelectInstallmentsToSetPaidProps {
	installments: IInstallmentProposal[];
	onConfirm: (selectedIds: string[]) => void;
	onClose: () => void;
}

export const ModalSelectInstallmentsToSetPaid = ({ installments, onConfirm, onClose }: ModalSelectInstallmentsToSetPaidProps) => {
	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	const sortedInstallments = useMemo(() => {
		return [...(installments ?? [])].sort((a, b) => (Number(a.number) || 0) - (Number(b.number) || 0));
	}, [installments]);

	const pendingIds = sortedInstallments.filter(i => (i.status ?? '').toLowerCase() === 'pending').map(i => i.id);
	const allSelected = selectedIds.length === pendingIds.length && pendingIds.length > 0;
	const partiallySelected = selectedIds.length > 0 && !allSelected;

	const toggleSelection = (id: string) => {
		// Só permite selecionar se for pending
		if (!pendingIds.includes(id)) return;
		setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
	};

	const toggleSelectAll = () => {
		if (allSelected) setSelectedIds([]);
		else setSelectedIds(pendingIds);
	};

	const handleConfirm = () => {
		if (selectedIds.length > 0) onConfirm(selectedIds);
	};

	return (
		<Dialog open onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
				<Typography variant="h6" fontWeight={400} color="primary.main">
					Selecione as parcelas para baixar
				</Typography>
				<IconButton onClick={onClose} size="small">
					<Close fontSize="small" />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ px: 4, pt: 2, maxHeight: "60vh" }}>
				{sortedInstallments.length === 0 ? (
					<Typography variant="body2" color="text.secondary">
						Nenhuma parcela disponível para baixa.
					</Typography>
				) : (
					<List dense disablePadding>
						<ListItem divider>
							<ListItemIcon>
								<Checkbox edge="start" checked={allSelected} indeterminate={partiallySelected} onChange={toggleSelectAll} />
							</ListItemIcon>
							<ListItemText primary="Selecionar todas" primaryTypographyProps={{ fontWeight: 500 }} />
						</ListItem>

						{sortedInstallments.map((item) => {
							const isPending = (item.status ?? "").toLowerCase() === "pending";
							const statusLabel = globalTranslateStatus(item.status ?? "");
							const primaryLabel =
								item.typeAmount === "discount"
									? "Desconto"
									: item.typeAmount === "add"
									? "Acréscimo"
									: `Parcela ${item.number}`;

							return (
								<ListItem key={item.id} divider>
									<ListItemIcon>
										<Checkbox
											edge="start"
											checked={selectedIds.includes(item.id)}
											onChange={() => toggleSelection(item.id)}
											disabled={!isPending}
										/>
									</ListItemIcon>
									<ListItemText
										primary={
											<Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
												<Stack direction="row" alignItems="center" gap={1}>
													<Typography variant="body2" fontWeight={500}>
														{primaryLabel}
													</Typography>
													<Typography variant="body2" color="text.primary">
														{globalFormatCurrencyBRL(item.amount)}
													</Typography>
												</Stack>
												<StatusInstallmentChip status={item.status ?? ""} label={statusLabel} />
											</Stack>
										}
										secondary={`Vencimento: ${format(new Date(item.dueDate + " 03:00:00"), "dd/MM/yyyy")}`}
									/>
								</ListItem>
							);
						})}
					</List>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 4, pb: 3, pt: 1 }}>
				<Stack direction="row" justifyContent="flex-end" width="100%">
					<Button variant="contained" color="primary" disabled={selectedIds.length === 0} onClick={handleConfirm}>
						Continuar
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};
